import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import { isOperator } from "../../auth/tier";
import { findOrThrow, loadErrorMessage } from "../../shared/loadOrError";
import type { TicketListSource } from "./TicketTable";
import type TicketDeskStore from "./TicketDeskStore";
import { NEW_TICKET_DIALOG } from "./clientNewTicketDialog";
import type { TicketRecord } from "./tickets.types";
import { isSessionEnded } from "stores/sessionGuard";
import { watchTicketChanges } from "./ticketsLive";

/** The newest this many — the tab says so when a client has more. */
export const CLIENT_TICKETS_PAGE = 100;

/**
 * Client 360's Tickets tab: every ticket ABOUT one client, open and closed,
 * newest first. Registered as `clientTicketsTabStore`; the ClientDetail route
 * pins it to the open client on enter and resets it on exit.
 *
 * It asks the API for `scope=all&clientId=<id>`. The API narrows `all` to
 * what the caller may read — everything for an operator, only the tickets
 * they filed, hold or whose team they are on for anyone else
 * (tickets.class.ts `find`) — so the tab never shows more than the desk's
 * "All" tab would; `isPartialView` lets it say so.
 *
 * A DELIBERATE DEVIATION from rule 1, for TicketDeskStore's reason: the
 * `tickets` ServiceStore's `entities` are shared with the desk and the Command
 * Center's count read, so they are not "this client's tickets". `rows` is the
 * find RESULT for this client, a snapshot every load replaces.
 *
 * It is a {@link TicketListSource}: a row opens the ticket on the desk
 * (`/tickets?open=<id>`), where its drawer, actions and attachments live.
 */
export default class ClientTicketsStore implements TicketListSource {
  rootStore: RootStore;

  clientId: string | null = null;
  rows: TicketRecord[] = [];
  /** The API's total for this client — null until a page arrives. */
  total: number | null = null;
  loading = false;
  loaded = false;
  error: string | null = null;
  /** Nothing is ever open in a drawer here (rows open on the desk). */
  get selectedId(): null {
    return null;
  }

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get ticketsService(): ServiceStoreType | null {
    return (
      ((this.rootStore as unknown as { ticketsStore?: ServiceStoreType })
        ?.ticketsStore as ServiceStoreType | null) ?? null
    );
  }

  /** Not an operator: the API shows only the tickets this person may read. */
  get isPartialView(): boolean {
    return !isOperator(this.rootStore);
  }

  /** More tickets about this client than the page holds. */
  get isTruncated(): boolean {
    return this.total !== null && this.total > this.rows.length;
  }

  /** Point the tab at a client and load it. A different client starts empty. */
  async initializeForClient(clientId: string): Promise<void> {
    if (clientId !== this.clientId) {
      this.reset();
      this.clientId = clientId;
    }
    this.watchLive();
    await this.load();
  }

  /** The live subscription's disposer (plain plumbing). */
  private stopLive: (() => void) | null = null;

  /**
   * Re-read the tab when a ticket about this client changes elsewhere (#3820):
   * one filed about it, or one already listed here (so a ticket moved to
   * another client leaves). Only tickets this person may read arrive at all
   * (see ticketsLive.ts). Torn down by `reset()`, which Client 360's onExit
   * and the sign-out purge both run.
   */
  private watchLive() {
    if (this.stopLive) return;
    this.stopLive = watchTicketChanges(
      this.rootStore,
      (ticket) =>
        // During the first read too: its answer may predate the change.
        (this.loaded || this.loading) &&
        this.clientId !== null &&
        (String(ticket.clientId ?? "") === this.clientId ||
          this.rows.some((row) => String(row.id) === String(ticket.id))),
      () => void this.load(),
    );
  }

  /**
   * Load (or reload) this client's tickets. Never rejects: a failure lands on
   * `error`, so the tab can tell "this client has no tickets" from "the
   * tickets could not be loaded".
   */
  async load(): Promise<void> {
    const clientId = this.clientId;
    if (!clientId) return;
    const seq = ++this.seq;
    this.loading = true;
    this.error = null;
    try {
      const res = await findOrThrow<
        { data?: TicketRecord[]; total?: number } | TicketRecord[] | null
      >(this.ticketsService, {
        query: {
          scope: "all",
          clientId,
          includeClosed: true,
          $limit: CLIENT_TICKETS_PAGE,
          $sort: { updatedAt: -1 },
        },
      });
      if (seq !== this.seq) return;
      if (res == null) throw new Error("The tickets could not be loaded.");
      runInAction(() => {
        this.rows = Array.isArray(res) ? res : (res.data ?? []);
        this.total = Array.isArray(res)
          ? res.length
          : typeof res.total === "number"
            ? res.total
            : this.rows.length;
        this.loaded = true;
      });
    } catch (err) {
      // Ended by a sign-out: not "loaded", and not a failure — the store was reset.
      if (seq !== this.seq || isSessionEnded(err)) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "This client's tickets could not be loaded.");
        this.loaded = true;
      });
    } finally {
      if (seq === this.seq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /**
   * Drop everything — on leaving Client 360, and on sign-out (RootStore
   * `purgeUserData` resets every app store with a `reset()`): another
   * person's view of a client's tickets is narrowed differently.
   */
  reset() {
    this.stopLive?.();
    this.stopLive = null;
    this.seq += 1;
    this.clientId = null;
    this.rows = [];
    this.total = null;
    this.loading = false;
    this.loaded = false;
    this.error = null;
  }

  /**
   * File a ticket ABOUT this client from the tab: the desk's own New ticket
   * dialog (registered on Client 360 too — clientNewTicketDialog.ts), its
   * draft preset to this client. The person can still change or clear it.
   *
   * PRESET AS ENGINEERING WORK (Feedback), not the desk's "Other". A ticket
   * filed from a client's page is almost always something the client needs
   * built or fixed, and "Other" is a kind nothing in engineering reads: it
   * reaches only the person named, and Gantry never lifts it. That default is
   * how a client ticket came to rest on the desk of somebody not running the
   * automation (White Board, 2026-10-27). The filer can still pick another
   * kind, and the dialog says what that kind will do.
   */
  startNewTicket() {
    const clientId = this.clientId;
    const desk = (this.rootStore as unknown as { ticketDeskStore?: TicketDeskStore }).ticketDeskStore;
    if (!clientId || !desk) return;
    desk.openNewTicket({ clientId, kind: "feedback" });
    this.rootStore.routerStore?.openDialog?.({ dialogName: NEW_TICKET_DIALOG });
  }

  /** Open a ticket on the desk, where its drawer and actions live. */
  open(id: number) {
    void this.rootStore.routerStore?.goTo?.("Tickets", {}, { open: String(id) });
  }

  /** Rows carry the API's resolved names; an id without one shows as `#id`. */
  userName(id: number | null | undefined): string {
    return id == null ? "" : `#${id}`;
  }
}
